<template>
  <div class="pg-mem">
    <div class="pg-mem__header">
      <h6 class="pg-mem__title">
        <i class="material-icons-round">psychology</i>
        Memory Interpretation Game
      </h6>
    </div>

    <!-- Memory Chunks Section -->
    <div class="pg-mem__section">
      <label class="pg-mem__section-label">
        <i class="material-icons-round">extension</i>
        Memory Chunks
      </label>

      <div class="pg-mem__list">
        <div
          v-for="(chunk, index) in sectionData.content.chunks"
          :key="index"
          class="pg-mem__item"
        >
          <div class="pg-mem__item-head">
            <span class="pg-mem__index">{{ index + 1 }}</span>
            <div class="pg-mem__item-actions">
              <button
                type="button"
                class="pg-mem__icon-btn pg-mem__icon-btn--danger"
                title="Delete chunk"
                @click="removeChunk(index)"
              >
                <i class="material-icons-round">delete</i>
              </button>
            </div>
          </div>

          <div class="pg-mem__item-body">
            <!-- Original Text Section -->
            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round pg-mem__field-ico">description</i>
                Original Text
              </label>
              <textarea
                v-model="chunk.originalText"
                class="p-input p-input--area"
                rows="3"
                placeholder="기억할 원문을 입력하세요..."
              />
            </div>

            <!-- Key Points Section -->
            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round pg-mem__field-ico">vpn_key</i>
                Key Points
              </label>
              <textarea
                v-model="chunk.keyPoints"
                class="p-input p-input--area"
                rows="2"
                placeholder="핵심 포인트를 입력하세요..."
              />
            </div>

            <!-- Expected Notes Section -->
            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round pg-mem__field-ico">check_box</i>
                Expected Notes
              </label>
              <textarea
                v-model="chunk.expectedNotes"
                class="p-input p-input--area"
                rows="3"
                placeholder="예상 메모 결과를 입력하세요..."
              />
            </div>

            <!-- Timing Settings -->
            <div class="p-form-grid p-form-grid--2 pg-mem__timing">
              <div class="p-field">
                <label class="p-field__label">
                  <i class="material-icons-round pg-mem__field-ico">visibility</i>
                  Exposure Time (seconds)
                </label>
                <input
                  type="number"
                  v-model.number="chunk.exposureTime"
                  class="p-input"
                  min="5"
                  max="60"
                  placeholder="노출 시간"
                />
              </div>
              <div class="p-field">
                <label class="p-field__label">
                  <i class="material-icons-round pg-mem__field-ico">schedule</i>
                  Recall Time (seconds)
                </label>
                <input
                  type="number"
                  v-model.number="chunk.recallTime"
                  class="p-input"
                  min="10"
                  max="120"
                  placeholder="기억 재생 시간"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="pg-mem__actions">
          <ABtn kind="yellow" icon="add" @click.prevent="addChunk">Add Memory Chunk</ABtn>
        </div>
      </div>
    </div>

    <!-- Total Session Time -->
    <div class="pg-mem__section">
      <label class="pg-mem__section-label">
        <i class="material-icons-round">hourglass_empty</i>
        Total Session Time
      </label>
      <div class="pg-mem__session">
        <div class="p-field pg-mem__session-field">
          <label class="p-field__label">
            <i class="material-icons-round pg-mem__field-ico">schedule</i>
            Session Duration (minutes)
          </label>
          <input
            type="number"
            v-model.number="sectionData.content.totalTime"
            class="p-input"
            min="5"
            max="60"
            placeholder="전체 세션 시간"
          />
          <p class="p-field__hint">
            <i class="material-icons-round pg-mem__hint-ico">info</i>
            권장 시간: 15-30분
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineModel, watch } from 'vue';
import ABtn from "@/components/Pineapple/ABtn.vue";

const sectionData = defineModel({ type: Object, required: true });

// 데이터 구조 초기화
watch(() => sectionData.value.content, (newContent) => {
  if (!newContent.chunks) {
    sectionData.value.content.chunks = [];
  }
  if (!newContent.totalTime) {
    sectionData.value.content.totalTime = 15;
  }
}, { immediate: true, deep: true });

const addChunk = () => {
  sectionData.value.content.chunks.push({
    originalText: '',
    keyPoints: '',
    expectedNotes: '',
    exposureTime: 15,
    recallTime: 30
  });
};

const removeChunk = (index) => {
  sectionData.value.content.chunks.splice(index, 1);
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-mem {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__header {
    padding-bottom: 12px;
    border-bottom: 1px solid $p-line;
  }

  &__title {
    font-size: 18px;
    font-weight: 700;
    color: $p-ink;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;

    > i {
      font-size: 22px;
      color: $p-point-deep;
    }
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__section-label {
    font-size: 14px;
    font-weight: 700;
    color: $p-ink;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 8px;
    border-bottom: 1px dashed $p-line;

    > i {
      font-size: 18px;
      color: $p-ink-mid;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__item {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
    transition: border-color $p-dur $p-ease;

    &:hover {
      border-color: $p-line-hard;
    }
  }

  &__item-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px dashed $p-line;
  }

  &__index {
    min-width: 28px;
    height: 28px;
    padding: 0 10px;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 800;
    display: inline-grid;
    place-items: center;
  }

  &__item-actions {
    display: flex;
    gap: 4px;
  }

  &__icon-btn {
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }

    &:hover:not(:disabled) {
      background: $p-panel-alt;
      color: $p-ink;
    }

    &--danger {
      color: $p-err;

      &:hover:not(:disabled) {
        background: $p-err-soft;
        color: $p-err;
      }
    }
  }

  &__item-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__field-ico {
    font-size: 14px !important;
    color: $p-ink-mid;
    vertical-align: middle;
    margin-right: 4px;
  }

  &__timing {
    padding: 12px;
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-sm;
  }

  &__session {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
  }

  &__session-field {
    max-width: 320px;
  }

  &__hint-ico {
    font-size: 12px !important;
    color: $p-info;
    vertical-align: middle;
    margin-right: 4px;
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
}
</style>
